import { deleteUndefined, isEqual, toPlain } from '@/core/utils/object-util';

export const Serializers = {
    boolean: {
        serialize(value) {
            return value ? 1 : 0;
        },
        deserialize(value) {
            return value === '1' || value === 1;
        },
    },
    received: {
        serialize(value) {
            switch (value) {
            case 'notArrived': return 'Not Arrived';
            case 'arrived': return 'Arrived';
            case 'partiallyReceived': return 'Partially Received';
            case 'fullyReceived': return 'Fully Received';
            default: return 'All ASNs';
            }
        },
        deserialize(value) {
            switch (value) {
            case 'Not Arrived': return 'notArrived';
            case 'Arrived': return 'arrived';
            case 'Partially Received': return 'partiallyReceived';
            case 'Fully Received': return 'fullyReceived';
            default: return 'all';
            }
        },
    },
    date: {
        serialize(value) {
            if (!value || typeof value === 'string') {
                return value;
            }
            return value && value.toJSON();
        },
        deserialize(value) {
            return value;
        },
    },
    number: {
        serialize(value) {
            return value;
        },
        deserialize(value) {
            return Number(value);
        },
    },
    documentStatus: {
        serialize(value) {
            switch (value) {
            case 'open': return 'OPEN';
            case 'closed': return 'CLOSED';
            case 'pastDue': return 'PAST DUE';
            default: return 'All';
            }
        },
        deserialize(value) {
            switch (value) {
            case 'open': return 'OPEN';
            case 'closed': return 'CLOSED';
            case 'pastDue': return 'PAST DUE';
            default: return 'All';
            }
        },
    },
};


function prepareSerializer(filterSerializer) {
    return Object.entries(filterSerializer || {}).reduce((serializers, [field, serializer]) => {
        const func = (typeof serializer === 'string') ? Serializers[serializer] : serializer;
        if (!func) {
            // eslint-disable-next-line no-console
            console.error(`Missing serializer for ${field}`);
        } else {
            // eslint-disable-next-line no-param-reassign
            serializers[field] = func;
        }
        return serializers;
    }, {});
}

export default class TableStateUrl {
    /* eslint-disable no-use-before-define */
    /**
     *
     * @param {VueRouter} router
     * @param {Object} [filterSerializer]
     * @param {Boolean} [trackPage]
     */
    constructor(router, filterSerializer = {}, trackPage = true) {
        this.router = router;
        this.trackPage = trackPage;
        this.filterSerializer = prepareSerializer(filterSerializer);
    }

    /**
     *
     * @param {TableModel} table
     */
    attach(table) {
        this.table = table;
    }


    restore() {
        const { query } = this.router.currentRoute;
        const state = stateFromQuery(query, this.filterSerializer, this.trackPage);
        this.table.init(state);
        this.table.setListener((oldState, newState, diff) => {
            this.store(newState, !!diff.page);
            return this.trackPage;
        });
    }

    store(state, historyBack = true) {
        const query = Object.assign({}, {
            page: (this.trackPage && state.page) || undefined,
        }, state.filters);

        const table = this.table;
        if (table && table.defaultPageSize !== state.pageSize) {
            query.pageSize = state.pageSize;
        }
        if (table && !isEqual(table.defaultOrder, state.order)) {
            query.order = prepareOrderQuery(state.order) || undefined;
        }

        deleteUndefined(query);

        Object.entries(this.filterSerializer).forEach(([field, ctrl]) => {
            if (query[field] !== undefined) {
                query[field] = ctrl.serialize(query[field]);
            }
        });

        if (historyBack) {
            this.router.push({ query });
        } else {
            this.router.replace({ query });
        }
    }

    update(route) {
        const queryState = stateFromQuery(route.query, this.filterSerializer, this.trackPage);
        this.table.setState(queryState);
        return queryState;
    }
}


function stateFromQuery(query, filterSerializer, trackPage) {
    const {
        page, pageSize, order,
        ...rest
    } = query;
    const filters = toPlain(rest || {});
    const config = {
        page: trackPage && Number(page),
        pageSize: Number(pageSize),
        filters,
        order: toPlain(parseOrderQuery(order)),
    };

    Object.entries(filterSerializer).forEach(([field, ctrl]) => {
        if (filters[field] !== undefined) {
            filters[field] = ctrl.deserialize(query[field]);
        }
    });

    if (!config.page || config.page < 1) {
        config.page = 1;
    }
    if (!config.pageSize || config.pageSize < 1) {
        config.pageSize = null;
    }
    return config;
}

function prepareOrderQuery(order) {
    if (!order || !order.field) {
        return '';
    }
    return `${order.dir === 'desc' ? '-' : ''}${order.field}`;
}

function parseOrderQuery(order) {
    if (!order) {
        return {};
    }
    if (order[0] === '-') {
        return { field: order.substr(1), dir: 'desc' };
    }
    return { field: order, dir: 'asc' };
}
